import { useMemo, useState } from 'react';
import { Route } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Button } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useCustomerJourneys } from '@/api/customer-journey';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { sortJourneys } from './sale-process-helpers';
import { JourneySelector } from './journey-selector';
import { JourneyPipelineStepper } from './journey-pipeline-stepper';
import { JourneyActions } from './journey-actions';
import { JourneyDealInfoSection } from './journey-deal-info-section';
import { JourneyNotesTimeline } from './journey-notes-timeline';
import { JourneyNegotiationSection } from './journey-negotiation-section';
import { JourneyStageBoard } from './board/journey-stage-board';
import { CreateJourneyDrawer } from './create-journey-drawer';

/**
 * Sale Process / Journey tab for the customer detail page — the sell-side
 * pipeline (journey → contract). Loads all of the customer's journeys, lets the
 * user pick the active one (selector for >1), and stacks: pipeline stepper +
 * lifecycle actions, a 2-col of Deal info + Notes, Deal negotiation
 * (journey→contract), and a B2B pipeline board across every journey.
 *
 * 1-1 with legacy datacentral-client SaleProcess in function + data; layout is
 * re-idiomed onto @vnetwork/ui (no ported shadcn markup, no read-drawers).
 */
export function SaleProcessTab({ customerId }: { customerId: number }) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const query = useCustomerJourneys(customerId);
  const journeys = useMemo(() => sortJourneys(query.data?.results ?? []), [query.data]);

  // Active journey: the explicit selection if still present, else the first.
  const active = useMemo(() => {
    if (selectedId != null) {
      const hit = journeys.find((j) => j.customer_journey_id === selectedId);
      if (hit) return hit;
    }
    return journeys[0];
  }, [journeys, selectedId]);

  if (query.isError) {
    return <TabError title="Failed to load journeys" onRetry={() => query.refetch()} />;
  }
  if (query.isLoading) {
    return <TabLoading rows={5} />;
  }

  if (journeys.length === 0) {
    return (
      <>
        <Card flat>
          <CardBody>
            <EmptyState
              icon={<Route size={28} />}
              title="No sales journey yet"
              description="Start tracking this customer's progress toward a contract."
              actions={<Button onClick={() => setCreating(true)}>Add journey</Button>}
            />
          </CardBody>
        </Card>
        <CreateJourneyDrawer customerId={customerId} open={creating} onOpenChange={setCreating} />
      </>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Pipeline + selector + lifecycle actions */}
      <Card flat>
        <CardHeader
          actions={
            <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>
              Add journey
            </Button>
          }
        >
          <CardTitle>Pipeline</CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {journeys.length > 1 ? (
            <JourneySelector
              journeys={journeys}
              activeJourneyId={active.customer_journey_id}
              onSelect={setSelectedId}
            />
          ) : null}
          <JourneyPipelineStepper journey={active} />
          <JourneyActions customerId={customerId} journey={active} />
        </CardBody>
      </Card>

      {/* Deal info + Notes side by side (stacks on narrow) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 'var(--vn-space-4)',
          alignItems: 'start',
        }}
      >
        <JourneyDealInfoSection customerId={customerId} journey={active} />
        <JourneyNotesTimeline customerId={customerId} journey={active} />
      </div>

      {/* Journey → contract negotiation */}
      <JourneyNegotiationSection customerId={customerId} journey={active} />

      {/* Interactive pipeline board (4 stages + per-stage actions) for the active journey */}
      <JourneyStageBoard customerId={customerId} journey={active} />

      <CreateJourneyDrawer customerId={customerId} open={creating} onOpenChange={setCreating} />
    </div>
  );
}

export default SaleProcessTab;
